import { createBaseFields, getTimestamp, addressSchema, locationSchema } from './baseSchema.js';

/**
 * ID Document schema
 */
export const idDocumentSchema = {
  storagePath: { _value: '', _optional: true, _immutable: true },
  verificationStatus: { _value: 'pending', _enum: ['pending', 'verified', 'rejected'], _immutable: true, _rule: 'oldData != null || this == "pending"' },
};

/**
 * Common fields shared across User, Employer, and Professional schemas
 */
export const commonUserFields = {
  phoneNumber: { _value: '', _rule: 'this.matches("^\\\\+[0-9]{10,15}$")', _immutable: true },
  email: { _value: '', _optional: true, _rule: 'this == "" || this.matches("^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\\\.[a-zA-Z]{2,}$")' },
  invitedBy: { _value: '', _optional: true, _immutable: true },
  inviteCodeUsed: { _value: '', _optional: true, _immutable: true },
};

/**
 * Validate common fields
 * @param {Object} data
 * @returns {Array} Array of error messages
 */
export const validateCommonFields = (data) => {
  const errors = [];
  if (!data.uid) errors.push('uid is required');
  if (!data.displayName) errors.push('displayName is required');
  if (!data.phoneNumber) errors.push('phoneNumber is required');
  if (data.email) {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(data.email)) {
      errors.push('valid email is required');
    }
  }
  return errors;
};

/**
 * Common profile fields shared between Employer and Professional
 */
export const commonProfileFields = {
  address: { ...addressSchema },
  profilePicture: {
    storagePath: { _value: '', _rule: 'this.size() > 0', _optional: true },
    url: { _value: '', _rule: 'this.size() > 0', _optional: true }
  },
};

/**
 * Validate profile fields
 * @param {Object} data
 * @returns {Array} Array of error messages
 */
export const validateProfileFields = (data) => {
  const errors = [];
  if (!data.address?.city) errors.push('address.city is required');
  if (!data.address?.pincode) errors.push('address.pincode is required');
  return errors;
};

/**
 * Common location fields shared between Employer and Professional
 */
export const commonLocationFields = {
  location: { ...locationSchema },
};

/**
 * Validate location fields
 * @param {Object} data
 * @returns {Array} Array of error messages
 */
export const validateLocationFields = (data) => {
  const errors = [];
  if (!data.location?.lat) errors.push('location.lat is required');
  if (!data.location?.lng) errors.push('location.lng is required');
  return errors;
};

/**
 * User collection schema (auth-related fields only)
 */
export const userSchema = {
  ...createBaseFields('', ''),
  ...commonUserFields,
  role: { _value: '', _enum: ['professional', 'employer'], _immutable: true },
  idDocument: { ...idDocumentSchema },
  onBoardingComplete: false,
};

/**
 * Create a user document
 * @param {string} uid - User ID
 * @param {string} phoneNumber - Phone number
 * @param {string} displayName - Display name
 * @param {string} role - User role ('professional' or 'employer')
 * @param {string} idStoragePath - Path to ID document in storage
 * @returns {Object} User document
 */
export const createUserDocument = ({
  uid,
  phoneNumber,
  displayName,
  role,
  idStoragePath,
  invitedBy,
  inviteCodeUsed,
  email,
}) => {
  const baseFields = createBaseFields(uid, displayName);

  return {
    ...baseFields,
    phoneNumber: phoneNumber || '',
    email: email || '',
    role: role || '',
    idDocument: {
      storagePath: idStoragePath || '',
      verificationStatus: 'pending',
    },
    onBoardingComplete: true,
    invitedBy: invitedBy || null,
    inviteCodeUsed: inviteCodeUsed || null,
  };
};

/**
 * Validate user document
 * @param {Object} data - User data to validate
 * @returns {Object} Validation result with isValid and errors
 */
export const validateUserDocument = (data) => {
  const errors = [...validateCommonFields(data)];
  if (!data.role || !['professional', 'employer'].includes(data.role)) {
    errors.push('valid role is required');
  }
  if (data.role !== 'employer' && !data.idDocument?.storagePath) {
    errors.push('idDocument.storagePath is required');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};
